import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../dashboard-data-block/dashboard-data-block.css';
import './dashboard-sim-summary-block.css';

/** Порядок строк и сопоставление с ответом API (label / operator). */
const OPERATOR_ROWS = [
  {
    key: 'megafon',
    label: 'Мегафон',
    aliases: ['мегафон', 'megafon'],
  },
  {
    key: 'mts',
    label: 'МТС',
    aliases: ['мтс', 'mts'],
  },
  {
    key: 'tele2',
    label: 'Теле2',
    aliases: ['теле2', 'tele2', 'т2', 't2'],
  },
  {
    key: 'yota',
    label: 'Yota',
    aliases: ['yota', 'йота'],
  },
  {
    key: 'beeline',
    label: 'Билайн',
    aliases: ['билайн', 'beeline', 'bee line'],
  },
];

const normalizeForMatch = (value) => String(value || '').trim().toLowerCase();

/** Совпадает с SimManager.js `normalizeOperatorValue` — те же подписи, что в колонке «Оператор». */
const normalizeOperatorLikeSimManager = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim();
  if (!normalized) return '';
  const lower = normalized.toLowerCase();
  if (lower === 'tele2' || lower === 'теле2' || lower === 't2') return 'Теле2';
  if (lower === 'megafon' || lower === 'мегафон') return 'Мегафон';
  if (lower === 'mts' || lower === 'мтс') return 'МТС';
  if (lower === 'beeline' || lower === 'билайн') return 'Билайн';
  if (lower === 'yota' || lower === 'йота') return 'Yota';
  return normalized;
};

const LABEL_TO_KEY = Object.fromEntries(
  OPERATOR_ROWS.map((def) => [normalizeForMatch(def.label), def.key])
);

const resolveRowKey = (row) => {
  const candidates = [row?.label, row?.operator].filter(
    (v) => v != null && String(v).trim() !== ''
  );
  for (const c of candidates) {
    const simNorm = normalizeOperatorLikeSimManager(c);
    if (simNorm) {
      const byLabel = LABEL_TO_KEY[normalizeForMatch(simNorm)];
      if (byLabel) return byLabel;
    }
    const raw = normalizeForMatch(c);
    if (!raw) continue;
    if (LABEL_TO_KEY[raw]) return LABEL_TO_KEY[raw];
    for (const def of OPERATOR_ROWS) {
      if (def.aliases.some((a) => raw === a || raw.includes(a))) {
        return def.key;
      }
      if (raw === normalizeForMatch(def.label)) {
        return def.key;
      }
    }
  }
  return null;
};

const emptyScope = () => ({
  total: 0,
  working: 0,
  reserve: 0,
  frozen: 0,
});

const emptyScopes = () => ({
  all: emptyScope(),
  msk: emptyScope(),
  spb: emptyScope(),
});

const coerceScope = (s) => ({
  total: Number(s?.total) || 0,
  working: Number(s?.working) || 0,
  reserve: Number(s?.reserve) || 0,
  frozen: Number(s?.frozen) || 0,
});

const pickScopes = (row) => {
  if (!row || typeof row !== 'object') {
    return emptyScopes();
  }
  return {
    all: { ...emptyScope(), ...coerceScope(row.all) },
    msk: { ...emptyScope(), ...coerceScope(row.msk) },
    spb: { ...emptyScope(), ...coerceScope(row.spb) },
  };
};

/**
 * Сводка по SIM: строки Мегафон…Билайн, столбцы три блока по 4 метрики (как на дашборде ddb).
 */
const DashboardSimSummaryBlock = ({
  title = 'Сводка по SIM картам',
  linkTo = '/sim-manager',
  summary = null,
}) => {
  const navigate = useNavigate();
  const isModifiedClick = (e) => e.metaKey || e.altKey || e.ctrlKey || e.shiftKey;

  const handleLinkClick = (e) => {
    if (e.button !== 0 || isModifiedClick(e)) {
      return;
    }
    e.preventDefault();
    navigate(linkTo);
  };

  const handleLinkKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(linkTo);
    }
  };

  const formatNumber = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = Number(val);
    if (Number.isNaN(num)) return val;
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const showTable = summary != null;

  const tableRows = useMemo(() => {
    const apiRows = Array.isArray(summary?.rows) ? summary.rows : [];
    const byKey = {};
    apiRows.forEach((row) => {
      const k = resolveRowKey(row);
      if (k) {
        byKey[k] = row;
      }
    });
    return OPERATOR_ROWS.map(({ key, label }) => ({
      key,
      label,
      scopes: pickScopes(byKey[key]),
    }));
  }, [summary]);

  const totalsScopes = useMemo(() => pickScopes(summary?.totals), [summary]);

  const buildScopeCells = (scopes, addDivider = false, keyPrefix = 'row') => {
    const { all, msk, spb } = scopes;
    const vdiv = ' dashboard-sim-summary__vdiv';
    const k = (suffix) => `${keyPrefix}-${suffix}`;
    return [
      <td key={k('all-t')} className="ddb__cell">{formatNumber(all.total)}</td>,
      <td key={k('all-w')} className="ddb__cell">{formatNumber(all.working)}</td>,
      <td key={k('all-r')} className="ddb__cell">{formatNumber(all.reserve)}</td>,
      <td key={k('all-f')} className="ddb__cell">{formatNumber(all.frozen)}</td>,
      <td key={k('msk-t')} className={`ddb__cell${addDivider ? vdiv : ''}`}>{formatNumber(msk.total)}</td>,
      <td key={k('msk-w')} className="ddb__cell">{formatNumber(msk.working)}</td>,
      <td key={k('msk-r')} className="ddb__cell">{formatNumber(msk.reserve)}</td>,
      <td key={k('msk-f')} className="ddb__cell">{formatNumber(msk.frozen)}</td>,
      <td key={k('spb-t')} className={`ddb__cell${vdiv}`}>{formatNumber(spb.total)}</td>,
      <td key={k('spb-w')} className="ddb__cell">{formatNumber(spb.working)}</td>,
      <td key={k('spb-r')} className="ddb__cell">{formatNumber(spb.reserve)}</td>,
      <td key={k('spb-f')} className="ddb__cell">{formatNumber(spb.frozen)}</td>,
    ];
  };

  return (
    <section className="ddb ddb--full dashboard-sim-summary" aria-label={`Блок: ${title}`}>
      <div className="ddb__header">
        <h2 className="ddb__title">{title}</h2>
        <a
          href={linkTo}
          className="ddb__link"
          onClick={handleLinkClick}
          onKeyDown={handleLinkKeyDown}
          tabIndex={0}
          aria-label="Перейти в раздел SIM-карты"
        >
          В раздел
          <svg
            className="ddb__link-icon"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M4.5 3L7.5 6L4.5 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>

      {!showTable ? (
        <p className="dashboard-sim-summary__empty">Загрузка сводки SIM…</p>
      ) : (
        <div className="ddb__table-wrap">
          <table className="ddb__table">
            <thead>
              <tr className="ddb__row ddb__row--header">
                <th className="ddb__cell ddb__cell--header" rowSpan={2} scope="col">
                  Оператор
                </th>
                <th className="ddb__cell ddb__cell--header dashboard-sim-summary__group-head" colSpan={4} scope="colgroup">
                  Всего
                </th>
                <th
                  className="ddb__cell ddb__cell--header dashboard-sim-summary__group-head dashboard-sim-summary__vdiv"
                  colSpan={4}
                  scope="colgroup"
                >
                  Мск
                </th>
                <th
                  className="ddb__cell ddb__cell--header dashboard-sim-summary__group-head dashboard-sim-summary__vdiv"
                  colSpan={4}
                  scope="colgroup"
                >
                  СПб
                </th>
              </tr>
              <tr className="ddb__row ddb__row--header">
                <th className="ddb__cell ddb__cell--header" scope="col">
                  Всего
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col">
                  Работает
                </th>
                <th
                  className="ddb__cell ddb__cell--header"
                  scope="col"
                  title="Сумма статусов «Резерв Мск» и «Резерв СПб»"
                >
                  Резерв
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col">
                  Заморожено
                </th>
                <th className="ddb__cell ddb__cell--header dashboard-sim-summary__vdiv" scope="col">
                  Всего Мск
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col">
                  Работает
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col" title="Статус SIM «Резерв Мск»">
                  Резерв Мск
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col">
                  Заморожено
                </th>
                <th className="ddb__cell ddb__cell--header dashboard-sim-summary__vdiv" scope="col">
                  Всего СПб
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col">
                  Работает
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col" title="Статус SIM «Резерв СПб»">
                  Резерв СПб
                </th>
                <th className="ddb__cell ddb__cell--header" scope="col">
                  Заморожено
                </th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => (
                <tr key={r.key} className="ddb__row">
                  <td className="ddb__cell ddb__cell--label">{r.label}</td>
                  {buildScopeCells(r.scopes, true, r.key)}
                </tr>
              ))}
              <tr className="ddb__row ddb__row--total">
                <td className="ddb__cell ddb__cell--label ddb__cell--total">Итого</td>
                {buildScopeCells(totalsScopes, true, 'total')}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default DashboardSimSummaryBlock;
